import {PageHead} from 'core/modules/PageHead';
import {Layout} from 'layouts/BasicLayout';
import {AppHead} from 'components/App/AppHead/AppHead';
import React, {useEffect} from 'react';
import {Provider} from 'react-redux';
import {TAppPropsWithLayout} from 'core/types';
import ErrorBoundary from 'modules/errors/components/ErrorBoundary';
import {CommonLayout} from 'layouts/CommonLayout';
import {ErrorLayout} from 'layouts/ErrorLayout';
import {PagesProgressProvider} from '@bprogress/next';
import {colorPrimary100} from 'core/constants/colors';
import {useAppState} from 'components/App/useAppState';
import {OnboardingProvider} from 'modules/onboarding/providers/OnboardingProvider';
import {resolveTheme} from 'core/theme/theme-routes.config';
export {reportWebVitals} from 'core/performance/webVitals';

// общие стили для глобальных компонентов
import 'styles/index.scss';
import 'rc-tooltip/assets/bootstrap.css';
import 'rc-drawer/assets/index.css';
import {useRouter} from 'next/router';
import themeStyle from './_app.module.scss';

const conditionalImportArticleCss = (designType: string | null) => {
    switch (designType) {
        case '2018': {
            // @ts-expect-error
            import('styles/article-2018.css');
            break;
        }
        case '2020': {
            // @ts-expect-error
            import('styles/article-2020.css');
            break;
        }
        default: {
            // @ts-expect-error
            import('styles/article-default.css');
        }
    }
};

const isStorybookPage = (pathname: string) => {
    return pathname.startsWith('/storybook');
};

// eslint-disable-next-line @typescript-eslint/naming-convention
function App({Component, ...rest}: TAppPropsWithLayout) {
    const router = useRouter();

    const {props, store, getLayout, designType} = useAppState({
        Component,
        rest,
    });

    // Тема страницы определяется реестром маршрутов и резолвится уже на SSR,
    // поэтому корректный data-theme попадает в разметку без мигания (FOUC).
    const theme = resolveTheme(router.pathname);

    useEffect(() => {
        if (!router.pathname.includes('[articleX]')) {
            return;
        }

        conditionalImportArticleCss(designType);
    }, [designType, router.pathname]);

    if (isStorybookPage(router.pathname)) {
        return <Component {...props} />;
    }

    const renderContent = () => (
        <ErrorBoundary>
            <Component {...props} />
        </ErrorBoundary>
    );

    return (
        <>
            <AppHead />
            <PageHead />
            <PagesProgressProvider
                height={'2px'}
                color={colorPrimary100}
                options={{showSpinner: false}}
                shallowRouting={true}
            >
                <Provider store={store}>
                    <OnboardingProvider>
                        <div data-theme={theme} className={themeStyle.themeRoot}>
                            <CommonLayout>
                                <ErrorLayout errorStatusCode={props.pageProps?.errorStatusCode}>
                                    {getLayout ? (
                                        getLayout(renderContent())
                                    ) : (
                                        <Layout {...props?.pageProps?.layoutProps}>{renderContent()}</Layout>
                                    )}
                                </ErrorLayout>
                            </CommonLayout>
                        </div>
                    </OnboardingProvider>
                </Provider>
            </PagesProgressProvider>
        </>
    );
}

export default App;
